import { batch, Component, createRef, h, signal, signalObject, tag } from "omi";
import classNames from "classnames";
import Dropzone, { DropzoneFile } from "dropzone";
import isEmpty from "lodash-es/isEmpty";
import { v4 as uuid } from "uuid";

import { NWCloseButton } from "@/wc/components/atoms/nw-button/closeButton.tsx";
import {
  FileType,
  isPortfolioItemFile,
  isPortfolioItemLink,
  isPortfolioItemProject,
  PortfolioDocument,
  PortfolioItemFile,
  PortfolioItemProject,
  PortfolioItemType,
} from "@/wc/pages/portfolio/store/models.ts";
import { NWButton } from "@/wc/components/atoms/nw-button";
import { NWDropzone } from "@/wc/components/atoms/nw-dropzone";
import { IconName, NWIcon } from "@/wc/components/atoms/nw-icon";
import { PortfolioStore } from "@/wc/pages/portfolio/store";
import { ModalProps } from "@/wc/components/modals";
import { isValidURL } from "@/utils/url.ts";
import { NWError } from "@/wc/components";
import { tailwind } from "@/tailwind";

type EditProps = { type: "edit"; document: PortfolioDocument };
type UploadProps = { type: "upload" };
type Props = ModalProps<EditProps | UploadProps>;

export type PortfolioEditModalData = EditProps;
export type PortfolioUploadModalData = UploadProps;

@tag("portfolio-upload-modal")
export class PortfolioUploadModal extends Component<Props> {
  static css = [tailwind];

  private titleInputRef = createRef<HTMLInputElement>();

  private portfolioTitle = signal("");
  private uploadUrl = signal<{ url: string; key: string } | undefined>(undefined);
  private website = signalObject<{ text: string; error: string | undefined }>({
    text: "",
    error: undefined,
  });

  private file = signal<DropzoneFile | undefined>(undefined);
  private projectType = signal<PortfolioItemType | undefined>(undefined);

  installed() {
    if (this.props.data.type === "edit") {
      const { document } = this.props.data;
      if (!document) return;

      this.portfolioTitle.value = document.title;

      if (isPortfolioItemLink(document)) {
        batch(() => {
          this.projectType.value = PortfolioItemType.URL;
          this.website.text.value = document.url!.url;
        });
      } else if (isPortfolioItemFile(document)) {
        batch(() => {
          this.projectType.value = PortfolioItemType.FILE;
          this.file.value = this.createFileFromDocument(document);
        });
      } else if (isPortfolioItemProject(document)) {
        this.projectType.value = PortfolioItemType.PROJECT;
      }
    }
  }

  uninstall(): void {
    this.titleInputRef.current = undefined;
    this.file.value = undefined;
    this.website.text.value = "";
    this.website.error.value = undefined;

    PortfolioStore.addDocumentMutation.error.value = undefined;
  }

  ready() {
    this.titleInputRef.current?.focus();
  }

  private createFileFromDocument = (document: PortfolioItemFile): DropzoneFile => {
    return {
      type: document.file!.fileType,
      name: document.file!.fileName,
      status: Dropzone.SUCCESS,
      dataURL: `/uploads/${document.file!.storageName}`,
    } as DropzoneFile;
  };

  private isFormValid = () => {
    const hasTitle = !isEmpty(this.portfolioTitle.value);

    switch (this.projectType.value) {
      case PortfolioItemType.FILE:
        return !!this.file.value && hasTitle;
      case PortfolioItemType.URL:
        return !isEmpty(this.website.text.value) && hasTitle;
      case PortfolioItemType.PROJECT:
        return hasTitle;
      default:
        return false;
    }
  };

  private handleTitleChange = (e: Event) => {
    const value = (e.target as HTMLInputElement).value;
    if (value.length > 60) return;
    this.portfolioTitle.value = value;
  };

  private handleWebsiteChange = (e: Event) => {
    const value = (e.target as HTMLInputElement).value;
    this.website.text.value = value;
  };

  private handleSubmit = async () => {
    if (this.portfolioTitle.value.length > 60) {
      return;
    }

    if (this.projectType.value === PortfolioItemType.FILE) {
      if (!this.file.value || (!isEmpty(this.file.value) && this.file.value.size <= 0)) {
        return;
      }
      this.website.error.value = undefined;
    } else if (this.projectType.value === PortfolioItemType.URL) {
      if (isEmpty(this.website.text.value) || !isValidURL(this.website.text.value)) {
        this.website.error.value = "Not a valid URL";
        return;
      }
      this.website.error.value = undefined;
    } else if (this.projectType.value === PortfolioItemType.PROJECT) {
      // continue;
    } else {
      return;
    }

    const doc = this.createPortfolioItem(this.projectType.value);

    let success: boolean;

    if (this.props.data.type === "edit") {
      success = await PortfolioStore.editDocument(doc);
    } else {
      success = !!(await PortfolioStore.addDocument(doc));
    }

    if (success) {
      this.props.close();
    }
  };

  private createPortfolioItem = (type: PortfolioItemType): PortfolioDocument => {
    const isEditMode = this.props.data.type === "edit";
    const id = isEditMode ? (this.props.data as EditProps).document.id : uuid();

    if (
      isEditMode &&
      type === PortfolioItemType.PROJECT &&
      isPortfolioItemProject((this.props.data as PortfolioEditModalData).document)
    ) {
      const document = (this.props.data as EditProps).document as PortfolioItemProject;

      return {
        type: PortfolioItemType.PROJECT,
        id,
        createdAt: document.createdAt,
        title: this.portfolioTitle.value,
        project: {
          id: document.project!.id,
        },
      };
    }

    if (type === PortfolioItemType.FILE) {
      if (isEditMode) {
        const document = (this.props.data as EditProps).document as PortfolioItemFile;

        return {
          type: PortfolioItemType.FILE,
          id,
          title: this.portfolioTitle.value,
          createdAt: document.createdAt,
          file: {
            storageName: this.uploadUrl.value?.key ?? document.file!.storageName,
            fileName: this.file.value!.name,
            fileType: this.file.value!.type as FileType,
          },
        };
      }

      return {
        type: PortfolioItemType.FILE,
        id,
        title: this.portfolioTitle.value,
        file: {
          storageName: this.uploadUrl.value?.key ?? this.file.value!.name,
          fileName: this.file.value!.name,
          fileType: this.file.value!.type as FileType,
        },
      };
    } else {
      return {
        type: PortfolioItemType.URL,
        id,
        title: this.portfolioTitle.value,
        url: { url: this.website.text.value },
        ...(isEditMode ? { createdAt: (this.props.data as EditProps).document.createdAt } : {}),
      };
    }
  };

  private handleUploadSuccess = (file: DropzoneFile | undefined) => {
    this.file.value = file;
  };

  private getUploadUrl = async () => {
    const res = await PortfolioStore.generateUploadUrl();
    if (!res) return undefined;
    this.uploadUrl.value = res;
    return res.url;
  };

  private renderProjectType = () => {
    if (this.projectType.value === PortfolioItemType.PROJECT) {
      // Project type documents can only be added from the share project flow for now.
      return undefined;
    }

    const content = () => {
      if (this.projectType.value === PortfolioItemType.FILE) {
        return this.renderPDFUpload();
      } else if (this.projectType.value === PortfolioItemType.URL) {
        return this.renderWebsiteUpload();
      }

      return undefined;
    };

    return (
      <div className="space-y-1.5 flex flex-col gap-4">
        <div class="flex flex-col gap-1.5">
          <label htmlFor="title" className="text-sm font-medium text-secondary">
            Project type <span className="text-error-600">*</span>
          </label>
          <p class="text-sm text-secondary">Chose one option below to upload your project</p>
        </div>

        <div class="w-full flex flex-row gap-6 h-28">
          <CardButton
            titleText="PDF upload"
            iconName="upload"
            active={this.projectType.value === PortfolioItemType.FILE}
            onClick={() => (this.projectType.value = PortfolioItemType.FILE)}
          />
          <CardButton
            titleText="URL link"
            iconName="link"
            active={this.projectType.value === PortfolioItemType.URL}
            onClick={() => (this.projectType.value = PortfolioItemType.URL)}
          />
        </div>

        {content()}
      </div>
    );
  };

  private renderPDFUpload = () => {
    return (
      <div>
        <NWDropzone
          getUploadUrl={this.getUploadUrl}
          onUploadSuccess={this.handleUploadSuccess}
          onAddFile={() => (this.file.value = undefined)}
          hasFile={!!this.file.value}
          fileName={this.file.value?.name}
          accept=".pdf,.png,.jpg,.jpeg"
          subtext="PDF, PNG or JPG (max. 50MB)"
          initialFile={this.file.value}
          maxSize={50}
        />
      </div>
    );
  };

  private renderWebsiteUpload = () => {
    const hasWebsiteError = !!this.website.error.value;
    return (
      <div className="space-y-1.5">
        <label htmlFor="website" className="text-sm font-medium text-secondary">
          Website
        </label>
        <div>
          <div className="flex w-full items-center h-10 bg-white shadow-xs">
            <p
              className={classNames(
                "border border-e-0 flex-shrink-0 z-10 select-none border-gray-200 inline-flex items-center py-2 px-4 rounded-s-lg text-tertiary",
              )}
            >
              https://
            </p>
            <div className="relative inline-flex w-full">
              <input
                id="website"
                tabIndex={3}
                className={classNames("py-2 pl-4 pr-9 w-full z-20 rounded-e-lg outline-none transition-all border", {
                  "border-error-300 focus:shadow-xse focus:border-error-600": hasWebsiteError,
                  "border-gray-200 focus:shadow-xss focus:border-brand-400": !hasWebsiteError,
                })}
                placeholder="my cool link..."
                maxLength="2048"
                onInputCapture={this.handleWebsiteChange}
                value={this.website.text.value}
              />
              {hasWebsiteError && (
                <NWIcon
                  name="alert-circle"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 z-50 text-error-600"
                />
              )}
            </div>
          </div>
          {hasWebsiteError && (
            <div class="inline-flex items-center space-x-2">
              <label
                className={classNames("text-sm mt-1.5", {
                  "text-error-600": hasWebsiteError,
                  "text-tertiary": !hasWebsiteError,
                })}
              >
                {this.website.error.value}
              </label>
            </div>
          )}
        </div>
      </div>
    );
  };

  render({ close }: Props) {
    const isEditMode = this.props.data.type === "edit";

    return (
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-[92%] mx-auto sm:w-screen"
        role="dialog"
        aria-modal="true"
      >
        <div className="p-6 space-y-5">
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{isEditMode ? "Edit project" : "Upload project to portfolio"}</h2>
              <NWCloseButton size="lg" onClick={close} tabIndex={-1}></NWCloseButton>
            </div>
            <p className="text-secondary">
              {isEditMode ? "Edit project title and uploaded content." : "Add a project title and upload content."}
            </p>
          </div>

          <div className="space-y-6">
            <div className="space-y-1.5">
              <label htmlFor="title" className="text-sm font-medium text-secondary">
                Project title <span className="text-error-600">*</span>
              </label>

              <input
                type="text"
                id="title"
                maxLength="60"
                ref={this.titleInputRef}
                tabIndex={1}
                className={classNames(
                  "w-full transition-all focus:outline-none border px-3.5 py-2.5 rounded-lg bg-white shadow-xs m-0",
                  "border-gray-200 text-primary focus:shadow-xss focus:border-brand-400",
                )}
                placeholder="A world class project name..."
                value={this.portfolioTitle.value}
                onInputCapture={this.handleTitleChange}
              />
            </div>

            {this.renderProjectType()}

            {PortfolioStore.addDocumentMutation.error.value && (
              <div class="">
                <NWError></NWError>
              </div>
            )}

            <div className="flex justify-between mt-8 sm:mt-0 sm:justify-end gap-3 rounded-b-lg">
              <NWButton size="lg" type="secondary" onClick={() => close()}>
                Cancel
              </NWButton>
              <NWButton
                size="lg"
                disabled={!this.isFormValid()}
                onClick={this.handleSubmit}
                loading={PortfolioStore.addDocumentMutation.isLoading.value}
                loadingText={isEditMode ? "Save changes" : "Upload project"}
              >
                {isEditMode ? "Save changes" : "Upload project"}
              </NWButton>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function CardButton({
  titleText,
  iconName,
  active,
  onClick,
}: {
  titleText: string;
  iconName: IconName;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      class={classNames(
        "group flex-1 p-4 border-2 border-transparent rounded-2xl flex flex-col justify-between items-start transition-all ",
        {
          "bg-brand-100  border-brand-500 ring-2 ring-brand-300": active,
          "bg-brand-50 hover:bg-brand-100": !active,
        },
      )}
    >
      <NWIcon
        name={iconName}
        class={classNames("transition-colors", {
          "text-leather": active,
          "text-brand-500 group-hover:text-brand-600": !active,
        })}
        width={24}
        height={24}
      ></NWIcon>
      <p
        class={classNames("text-sm font-medium transition-colors", {
          "text-leather": active,
          "text-brand-700": !active,
        })}
      >
        {titleText}
      </p>
    </button>
  );
}
